import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Calendar, MapPin, Users, Trophy, AlertTriangle, CheckCircle2,
  CreditCard, Smartphone, Loader2, Copy,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  parent_name: "",
  parent_email: "",
  parent_phone: "",
  child_name: "",
  child_dob: "",
  child_gender: "male",
  age_group: "",
  payment_method: "paynow",
};

export default function PublicEventPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // { paynow_qr_datauri, payment_reference, ...}

  const stripeStatus = searchParams.get("status");  // 'success' | 'cancelled' from Stripe redirect

  // Canonical pricing pulled from backend — never compute on the frontend.
  // Refreshes whenever payment_method changes so display + Stripe charge
  // are mathematically guaranteed to match. (Drift-prevention contract.)
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  useEffect(() => {
    if (!event || !event.is_registerable) return;
    let cancelled = false;
    (async () => {
      setPricingLoading(true);
      try {
        const r = await axios.post(`${API}/public/events/${slug}/pricing-preview`, {
          payment_method: form.payment_method,
        });
        if (!cancelled) setPricing(r.data);
      } catch (e) {
        if (!cancelled) {
          console.error("pricing-preview failed", e);
          setPricing(null);
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [event, slug, form.payment_method]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/events/by-slug/${slug}`);
        if (!cancelled) {
          setEvent(r.data);
          // Pre-select first age group if event has any
          if (r.data?.age_groups?.length && !form.age_group) {
            setForm((f) => ({ ...f, age_group: r.data.age_groups[0] }));
          }
        }
      } catch (e) {
        if (!cancelled && e.response?.status === 404) setNotFound(true);
        else if (!cancelled) toast.error("Failed to load event");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const today = new Date().toISOString().slice(0, 10);

  const registrationBlocker = (() => {
    if (!event) return null;
    // Prefer backend-supplied lifecycle state (single source of truth, also
    // what AS reads). Fall back to local derivation only if backend is older.
    if (event.is_registerable === false) {
      return { kind: event.registration_status || "blocked", message: event.registration_reason || "Registration unavailable." };
    }
    if (event.registration_open_date && today < event.registration_open_date) {
      return { kind: "not_yet", message: `Registration opens on ${event.registration_open_date}.` };
    }
    if (event.registration_close_date && today > event.registration_close_date) {
      return { kind: "closed", message: `Registration closed on ${event.registration_close_date}.` };
    }
    if (event.end_date && today > event.end_date) {
      return { kind: "past", message: "This event has already finished." };
    }
    if (event.capacity != null && event.registration_count >= event.capacity) {
      return { kind: "full", message: "This event is fully booked." };
    }
    return null;
  })();

  const submit = async () => {
    // Minimal client-side validation
    const required = ["parent_name", "parent_email", "parent_phone", "child_name", "child_dob", "child_gender"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error(`Please fill in: ${k.replaceAll("_", " ")}`);
        return;
      }
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.parent_email)) {
      toast.error("Please enter a valid email");
      return;
    }

    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/public/events/${slug}/register`,
        { ...form, origin_url: window.location.origin }
      );
      if (form.payment_method === "stripe" && r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setResult(r.data);
      toast.success("Registration received — please complete payment.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300 px-4">
        <Card className="bg-slate-900/60 border-slate-800 p-10 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Event not found</h1>
          <p className="text-sm text-slate-400 mb-5">
            This event isn&apos;t published yet, or it has been removed. Check the link or contact
            <a href="mailto:academy@sccfootball.com" className="text-blue-400 hover:underline ml-1">academy@sccfootball.com</a>.
          </p>
          <Link to="/" className="text-blue-400 hover:underline text-sm">Back to portal</Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="mb-8" data-testid="event-hero">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge className={event.category === "tournament"
              ? "bg-purple-500/15 text-purple-300 border border-purple-500/40"
              : "bg-orange-500/15 text-orange-300 border border-orange-500/40"}>
              {event.category === "tournament" ? <><Trophy className="w-3 h-3 mr-1" /> Tournament</> : <><Calendar className="w-3 h-3 mr-1" /> Camp</>}
            </Badge>
            {event.capacity != null && (
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-300">
                <Users className="w-3 h-3 mr-1" />
                {event.registration_count || 0} / {event.capacity} booked
              </Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="event-name">
            {event.name}
          </h1>
          <div className="text-slate-400 text-sm flex flex-wrap gap-x-5 gap-y-1 mb-3">
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{event.start_date} → {event.end_date}</span>
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{event.location}</span>}
            <span className="font-semibold text-white">${event.price?.toFixed?.(2) ?? event.price} {event.currency}</span>
            {(event.age_groups || []).length > 0 && <span>Ages: {(event.age_groups || []).join(", ")}</span>}
          </div>
          {event.description && (
            <p className="text-slate-300 text-base whitespace-pre-line" data-testid="event-description">
              {event.description}
            </p>
          )}
        </div>

        {/* Stripe redirect outcome banner */}
        {stripeStatus === "success" && (
          <Card className="bg-emerald-500/10 border border-emerald-500/40 p-4 mb-6 text-emerald-200" data-testid="stripe-success-banner">
            <CheckCircle2 className="w-5 h-5 inline mr-2 text-emerald-400" />
            Payment received — thank you! You&apos;ll get a confirmation email shortly.
          </Card>
        )}
        {stripeStatus === "cancelled" && (
          <Card className="bg-amber-500/10 border border-amber-500/40 p-4 mb-6 text-amber-200" data-testid="stripe-cancelled-banner">
            Payment was cancelled. You can try again below or pick PayNow.
          </Card>
        )}

        {/* PayNow QR result panel */}
        {result && result.payment_method === "paynow" && (
          <Card className="bg-slate-900/60 border border-emerald-500/40 p-6 mb-6 text-center" data-testid="paynow-result">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-1">Almost there — scan to pay</h2>
            <p className="text-sm text-slate-400 mb-4">
              We&apos;ve also emailed you this QR + reference in case you need to pay later.
            </p>
            {result.paynow_qr_datauri && (
              <div className="bg-white inline-block p-3 rounded mb-4">
                <img src={result.paynow_qr_datauri} alt="PayNow QR" className="w-64 h-64" data-testid="paynow-qr-image" />
              </div>
            )}
            <div className="text-sm space-y-1">
              <div><span className="text-slate-500">Reference:</span> <span className="font-mono text-white">{result.payment_reference}</span>
                <button className="text-slate-400 hover:text-white ml-2" onClick={async () => { await navigator.clipboard.writeText(result.payment_reference); toast.success("Copied"); }}>
                  <Copy className="w-3 h-3 inline" />
                </button>
              </div>
              <div><span className="text-slate-500">Amount:</span> <span className="text-white font-bold">${result.amount.toFixed(2)} {result.currency}</span></div>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Once the payment lands we&apos;ll confirm your spot. Allow up to 1 working day. <br />
              Questions? <a href="mailto:academy@sccfootball.com" className="text-blue-400 hover:underline">academy@sccfootball.com</a>
            </p>
          </Card>
        )}

        {/* Registration form OR blocker */}
        {!result && (
          registrationBlocker ? (
            <Card className="bg-amber-500/5 border border-amber-500/40 p-8 text-center" data-testid="event-blocker">
              <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white mb-2">
                {registrationBlocker.kind === "full" ? "Fully booked" : "Registration closed"}
              </h2>
              <p className="text-slate-300 mb-2">{registrationBlocker.message}</p>
              <p className="text-sm text-slate-500 mt-4">
                Get in touch: <a href="mailto:academy@sccfootball.com" className="text-blue-400 hover:underline">academy@sccfootball.com</a>
              </p>
            </Card>
          ) : (
            <Card className="bg-slate-900/60 border-slate-800 p-6" data-testid="event-form">
              <h2 className="text-xl font-bold text-white mb-1">Register</h2>
              <p className="text-sm text-slate-400 mb-5">One child per registration. Flat ${event.price?.toFixed?.(2)} {event.currency}.</p>

              <div className="space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Parent</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Full name *</label>
                      <Input value={form.parent_name} onChange={(e) => setForm((f) => ({ ...f, parent_name: e.target.value }))} className="bg-slate-800 border-slate-700" data-testid="form-parent-name" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Email *</label>
                      <Input type="email" value={form.parent_email} onChange={(e) => setForm((f) => ({ ...f, parent_email: e.target.value }))} className="bg-slate-800 border-slate-700" data-testid="form-parent-email" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs text-slate-400 block mb-1">Phone *</label>
                      <Input value={form.parent_phone} onChange={(e) => setForm((f) => ({ ...f, parent_phone: e.target.value }))} placeholder="+65…" className="bg-slate-800 border-slate-700" data-testid="form-parent-phone" />
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Child</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-xs text-slate-400 block mb-1">Full name *</label>
                      <Input value={form.child_name} onChange={(e) => setForm((f) => ({ ...f, child_name: e.target.value }))} className="bg-slate-800 border-slate-700" data-testid="form-child-name" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Date of birth *</label>
                      <Input type="date" value={form.child_dob} onChange={(e) => setForm((f) => ({ ...f, child_dob: e.target.value }))} className="bg-slate-800 border-slate-700" data-testid="form-child-dob" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Gender *</label>
                      <div className="flex gap-2">
                        {["male", "female"].map((g) => (
                          <button key={g} type="button"
                            onClick={() => setForm((f) => ({ ...f, child_gender: g }))}
                            className={`flex-1 px-3 py-2 rounded text-sm font-medium border transition ${
                              form.child_gender === g
                                ? "bg-blue-500/20 border-blue-500/60 text-blue-200"
                                : "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200"
                            }`}
                            data-testid={`form-child-gender-${g}`}
                          >
                            {g.charAt(0).toUpperCase() + g.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(event.age_groups || []).length > 0 && (
                      <div className="sm:col-span-2">
                        <label className="text-xs text-slate-400 block mb-1">Age group</label>
                        <div className="flex flex-wrap gap-2">
                          {(event.age_groups || []).map((ag) => (
                            <button key={ag} type="button"
                              onClick={() => setForm((f) => ({ ...f, age_group: ag }))}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                                form.age_group === ag
                                  ? "bg-blue-500/20 border-blue-500/60 text-blue-200"
                                  : "bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200"
                              }`}
                              data-testid={`form-age-${ag}`}
                            >
                              {ag}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Payment method</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button"
                      onClick={() => setForm((f) => ({ ...f, payment_method: "paynow" }))}
                      className={`px-4 py-3 rounded border text-left transition ${
                        form.payment_method === "paynow"
                          ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-100"
                          : "bg-slate-800 border-slate-700 text-slate-300 hover:text-white"
                      }`}
                      data-testid="form-pay-paynow"
                    >
                      <div className="flex items-center gap-2 font-medium"><Smartphone className="w-4 h-4" /> PayNow</div>
                      <div className="text-xs text-slate-500 mt-1">Scan QR with any SG banking app</div>
                    </button>
                    <button type="button"
                      onClick={() => setForm((f) => ({ ...f, payment_method: "stripe" }))}
                      className={`px-4 py-3 rounded border text-left transition ${
                        form.payment_method === "stripe"
                          ? "bg-indigo-500/15 border-indigo-500/60 text-indigo-100"
                          : "bg-slate-800 border-slate-700 text-slate-300 hover:text-white"
                      }`}
                      data-testid="form-pay-stripe"
                    >
                      <div className="flex items-center gap-2 font-medium"><CreditCard className="w-4 h-4" /> Credit / Debit Card</div>
                      <div className="text-xs text-slate-500 mt-1">Secure checkout via Stripe</div>
                    </button>
                  </div>
                </div>

                {/* Canonical price breakdown from backend — rendered
                    verbatim (no JS math). Stripe adds a 3.4% Credit Card
                    Processing fee; PayNow shows just the base price. */}
                <div className="bg-slate-800/40 rounded text-sm" data-testid="pricing-breakdown">
                  {pricingLoading && !pricing ? (
                    <div className="px-4 py-3 text-slate-400 text-xs">Calculating…</div>
                  ) : pricing ? (
                    <div className="divide-y divide-slate-700/60">
                      {pricing.line_items.map((li, i) => (
                        <div
                          key={i}
                          className={`flex items-center justify-between px-4 py-2 ${li.kind === "card_fee" ? "text-amber-200/90" : "text-slate-200"}`}
                          data-testid={`pricing-line-${li.kind}`}
                        >
                          <span className="text-xs">{li.label}</span>
                          <span className="font-mono text-xs">${li.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-800/60">
                        <span className="text-slate-300 font-medium">Total</span>
                        <span className="text-xl font-bold text-white" data-testid="pricing-total">
                          ${pricing.total.toFixed(2)} {pricing.currency}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-3 flex items-center justify-between">
                      <span className="text-slate-400">Total</span>
                      <span className="text-xl font-bold text-white">${event.price?.toFixed?.(2)} {event.currency}</span>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white text-base py-6"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="form-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                  ) : form.payment_method === "stripe" ? (
                    "Continue to card payment"
                  ) : (
                    "Register & show PayNow QR"
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By registering you agree to our terms. You&apos;ll receive a confirmation email.
                </p>
              </div>
            </Card>
          )
        )}
      </div>
    </div>
  );
}
